/**
 * CSS Input Parser - Parse CSS variables from imported CSS
 * Adapted from shadcn-studio for OptiDev
 */

import type { ThemeStyleProps } from "@/types/theme.types";

// Common variable names that are not colors
const COMMON_STYLES = [
  "radius",
  "spacing",
  "font-sans",
  "font-serif",
  "font-mono",
  "letter-spacing",
  "shadow-color",
  "shadow-opacity",
  "shadow-blur",
  "shadow-spread",
  "shadow-offset-x",
  "shadow-offset-y",
];

// All valid CSS variable names
const variableNames = [
  // Core colors
  "background",
  "foreground",
  "card",
  "card-foreground",
  "popover",
  "popover-foreground",
  "primary",
  "primary-foreground",
  "secondary",
  "secondary-foreground",
  "muted",
  "muted-foreground",
  "accent",
  "accent-foreground",
  "destructive",
  "destructive-foreground",
  "border",
  "input",
  "ring",
  // Charts
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
  // Sidebar
  "sidebar",
  "sidebar-foreground",
  "sidebar-primary",
  "sidebar-primary-foreground",
  "sidebar-accent",
  "sidebar-accent-foreground",
  "sidebar-border",
  "sidebar-ring",
  // Common styles
  ...COMMON_STYLES,
];

const VARIABLE_PREFIX = "--";

/**
 * Parse CSS input and extract light/dark color variables
 */
function parseCssInput(input: string): {
  lightColors: Partial<ThemeStyleProps>;
  darkColors: Partial<ThemeStyleProps>;
} {
  const lightColors: Partial<ThemeStyleProps> = {};
  const darkColors: Partial<ThemeStyleProps> = {};

  try {
    // Extract content from :root and .dark blocks
    const rootContent = extractCssBlockContent(input, ":root");
    const darkContent = extractCssBlockContent(input, ".dark");

    // Parse root content for light theme
    if (rootContent) {
      parseColorVariables(rootContent, lightColors);
    }

    // Parse dark content for dark theme
    if (darkContent) {
      parseColorVariables(darkContent, darkColors);
    }
  } catch (error) {
    console.error("Error parsing CSS input:", error);
  }

  return { lightColors, darkColors };
}

/**
 * Extract content from a CSS block (e.g., :root { ... })
 */
function extractCssBlockContent(
  input: string,
  selector: string
): string | null {
  const regex = new RegExp(`${escapeRegExp(selector)}\\s*{([^}]+)}`);
  return input.match(regex)?.[1]?.trim() || null;
}

/**
 * Parse color variables from CSS content
 */
function parseColorVariables(
  cssContent: string,
  target: Partial<ThemeStyleProps>
): void {
  const variableDeclarations = cssContent.match(/--[^:]+:\s*[^;]+/g) || [];

  variableDeclarations.forEach((declaration) => {
    const [name, value] = declaration.split(":").map((part) => part.trim());
    const cleanName = name.replace(VARIABLE_PREFIX, "");

    if (variableNames.includes(cleanName)) {
      if (COMMON_STYLES.includes(cleanName)) {
        // For non-color variables, store as is
        target[cleanName as keyof ThemeStyleProps] = value;
        return;
      }

      // For color variables, process the value
      const colorValue = processColorValue(value);
      target[cleanName as keyof ThemeStyleProps] = colorValue;
    }
  });
}

/**
 * Process a color value to normalize format
 */
function processColorValue(value: string): string {
  // Handle HSL values with space-separated components
  if (/^\d/.test(value)) {
    return `hsl(${value})`;
  }

  // For all other formats (rgb, oklch, hsl, hex), keep as is
  value = value.trim();

  // Ensure proper spacing in rgb/rgba values
  if (value.startsWith("rgb")) {
    return value.replace(/\s+/g, " ");
  }

  // Ensure proper spacing in hsl/hsla values
  if (value.startsWith("hsl")) {
    return value.replace(/\s+/g, " ");
  }

  // Ensure proper spacing in oklch values
  if (value.startsWith("oklch")) {
    return value.replace(/\s+/g, " ");
  }

  return value;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Parse shadow variables from CSS content
 */
function parseShadowVariables(cssContent: string): {
  lightShadows: Partial<ThemeStyleProps>;
  darkShadows: Partial<ThemeStyleProps>;
} {
  const lightShadows: Partial<ThemeStyleProps> = {};
  const darkShadows: Partial<ThemeStyleProps> = {};

  try {
    // Extract content from :root and .dark blocks
    const rootContent = extractCssBlockContent(cssContent, ":root");
    const darkContent = extractCssBlockContent(cssContent, ".dark");

    // Parse shadow variables from light theme
    if (rootContent) {
      extractBasicShadowProperties(rootContent, lightShadows);
    }

    // Parse shadow variables from dark theme
    if (darkContent) {
      extractBasicShadowProperties(darkContent, darkShadows);
    }
  } catch (error) {
    console.error("Error parsing shadow variables:", error);
  }

  return { lightShadows, darkShadows };
}

/**
 * Extract basic shadow properties from CSS content
 */
function extractBasicShadowProperties(
  cssContent: string,
  target: Partial<ThemeStyleProps>
): void {
  // Create regex to match all shadow properties
  const directPropertiesRegex =
    /--shadow-(color|opacity|blur|spread|offset-x|offset-y):\s*([^;]+);/g;

  // Set default values
  target["shadow-offset-x"] = "0";
  target["shadow-offset-y"] = "1px";
  target["shadow-blur"] = "3px";
  target["shadow-spread"] = "0px";
  target["shadow-color"] = "hsl(0 0% 0%)";
  target["shadow-opacity"] = "0.1";

  // Check if any direct shadow properties exist
  let match;
  let foundDirectProperties = false;

  while ((match = directPropertiesRegex.exec(cssContent)) !== null) {
    const property = `shadow-${match[1]}`;
    const value = match[2].trim();
    target[property as keyof ThemeStyleProps] = value;
    foundDirectProperties = true;
  }

  if (foundDirectProperties) {
    return;
  }

  // Parse from shadow definition if no direct properties found
  const shadowRegex = /--shadow(?:-[a-z0-9]+)?:\s*([^;]+);/g;
  const shadowValues: Array<{
    value: string;
    hasComma: boolean;
    name: string;
  }> = [];

  while ((match = shadowRegex.exec(cssContent)) !== null) {
    shadowValues.push({
      value: match[1],
      hasComma: match[1].includes(","),
      name: match[0].match(/--([^:]+):/)?.[1] || "",
    });
  }

  if (shadowValues.length === 0) {
    return;
  }

  // Sort to prefer single-layer shadows for easier parsing
  const orderedNames = ["shadow-2xs", "shadow-xs", "shadow-sm", "shadow"];

  const bestShadow =
    shadowValues
      .filter((s) => !s.hasComma)
      .sort((a, b) => {
        const aIndex = orderedNames.indexOf(a.name);
        const bIndex = orderedNames.indexOf(b.name);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      })[0] || shadowValues[0];

  if (!bestShadow) return;

  const parts = bestShadow.value.split(",")[0].trim().split(/\s+/);

  if (parts.length >= 5) {
    target["shadow-offset-x"] = parts[0];
    target["shadow-offset-y"] = parts[1];
    target["shadow-blur"] = parts[2];
    target["shadow-spread"] = parts[3];

    const colorPart = parts.slice(4).join(" ");
    const colorRegex = /(?:hsl|rgb|oklch)\(([^/]+)(?:\/\s*([^)]+))?\)/;
    const colorMatch = colorPart.match(colorRegex);

    if (colorMatch) {
      const [, colorValues, opacity] = colorMatch;
      const colorType = colorPart.substring(0, 3);
      target["shadow-color"] = `${colorType}${colorType === "okl" ? "ch" : ""}(${colorValues})`;

      if (opacity) {
        target["shadow-opacity"] = formatOpacity(opacity);
      }
    }
  }
}

/**
 * Format opacity value from different formats
 */
function formatOpacity(opacityStr: string): string {
  const cleanOpacity = opacityStr.replace("%", "").trim();

  if (cleanOpacity.includes(".")) {
    return parseFloat(cleanOpacity).toString();
  } else if (!isNaN(parseInt(cleanOpacity, 10))) {
    return (parseInt(cleanOpacity, 10) / 100).toString();
  }

  return "0.1";
}

/**
 * Parse letter spacing from CSS content
 */
function parseLetterSpacing(cssContent: string): string {
  const letterSpacing =
    cssContent.match(/--tracking-normal:\s*([^;]+);/)?.[1] ||
    cssContent.match(/--letter-spacing:\s*([^;]+);/)?.[1] ||
    "0em";
  return letterSpacing;
}

export { parseCssInput, parseShadowVariables, parseLetterSpacing };
